import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import StockService from '../../services/stock.service';

const StockList: React.FC = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStocks();
  }, []);

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await StockService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setStocks(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this stock entry?')) {
      try {
        await StockService.delete(id);
        fetchStocks();
      } catch (error) {
        console.error('Error deleting stock:', error);
      }
    }
  };

  const getStockStatus = (stock: any) => {
    if (stock.reorderPoint && stock.quantity <= stock.reorderPoint) {
      return { label: 'Reorder', color: 'error' as const };
    }
    if (stock.minQuantity && stock.quantity <= stock.minQuantity) {
      return { label: 'Low', color: 'warning' as const };
    }
    return { label: 'In Stock', color: 'success' as const };
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Stock Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/stock/create')}
        >
          New Stock Entry
        </Button>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Min Qty</TableCell>
                <TableCell align="right">Reorder Point</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.map((stock) => {
                const stockAttrs = stock.attributes;
                const status = getStockStatus(stockAttrs);
                return (
                  <TableRow key={stock.id} hover>
                    <TableCell>{stockAttrs?.product?.data?.attributes?.name || '-'}</TableCell>
                    <TableCell>{stockAttrs?.product?.data?.attributes?.sku || '-'}</TableCell>
                    <TableCell>
                      {stockAttrs?.warehouse?.data
                        ? `${stockAttrs.warehouse.data.attributes.name} (${stockAttrs.warehouse.data.attributes.code})`
                        : '-'}
                    </TableCell>
                    <TableCell>{stockAttrs?.location || '-'}</TableCell>
                    <TableCell align="right">{stockAttrs?.quantity}</TableCell>
                    <TableCell align="right">{stockAttrs?.minQuantity || '-'}</TableCell>
                    <TableCell align="right">{stockAttrs?.reorderPoint || '-'}</TableCell>
                    <TableCell>
                      <Chip label={status.label} size="small" color={status.color} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/stock/${stock.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(stock.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && stocks.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No stock entries found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default StockList;

